import React, { useState } from 'react';
import { ShoppingBagIcon, CreditCardIcon, ArrowUturnLeftIcon, EyeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useQuery, useMutation, usePagination } from '../../hooks';
import { ordersAPI, paymentsAPI, returnsAPI } from '../../services/api';
import {
  Card, SectionHeader, Button, Badge, StatusBadge, Modal, Select,
  Pagination, LoadingPage, EmptyState, Tabs,
} from '../../components/common/UI';
import { format } from 'date-fns';

const STATUS_OPTS_BIZ = ['pending', 'processing', 'confirmed', 'shipped', 'out_for_delivery', 'delivered', 'cancelled', 'return_requested', 'return_approved', 'pickup_scheduled', 'return_picked', 'refunded'];

const STATUS_FLOW = {
  pending: ['processing', 'cancelled'],
  processing: ['confirmed', 'cancelled'],
  confirmed: ['shipped', 'cancelled'],
  shipped: ['out_for_delivery', 'cancelled'],
  out_for_delivery: ['delivered', 'cancelled'],
  delivered: ['return_requested'],
  return_requested: ['return_approved', 'cancelled'],
  return_approved: ['pickup_scheduled', 'return_picked', 'cancelled'],
  pickup_scheduled: ['return_picked', 'cancelled'],
  return_picked: ['refunded'],
  refunded: [],
  cancelled: [],
};

const ORDER_TABS = [
  { value: '', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'return_requested', label: 'Returns' },
  { value: 'refunded', label: 'Refunded' },
];

export const OrderDetailModal = ({ orderId, open, onClose, onUpdated, readOnly = false, hideUpdateSection = false }) => {
  const isReadOnly = readOnly || hideUpdateSection;
  const { data: order, loading, refetch } = useQuery(() => ordersAPI.detail(orderId), [orderId], { enabled: !!orderId && open });
  console.log('OrderDetailModal:', { orderId, loading, order });
  const [status, setStatus] = useState('');
  const [note, setNote] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [updatingItemId, setUpdatingItemId] = useState(null);

  const { mutate: update, loading: updating } = useMutation(
    () => ordersAPI.updateStatus(orderId, {
      status,
      note: note || `Status updated to ${status}`,
      refund_amount: status === 'refunded' ? refundAmount : undefined
    }),
    {
      successMsg: 'Status updated',
      onSuccess: () => {
        refetch();
        onUpdated?.();
        onClose();
        setStatus('');
        setNote('');
        setRefundAmount('');
      }
    }
  );

  const handleItemStatusChange = async (itemId, targetStatus) => {
    if (!targetStatus) return;
    setUpdatingItemId(itemId);
    try {
      await ordersAPI.updateItemStatus(orderId, itemId, {
        status: targetStatus,
        note: `Item status updated to ${targetStatus}`
      });
      toast.success(`Item status updated to ${targetStatus.replace(/_/g, ' ')}`);
      refetch();
      onUpdated?.();
    } catch (err) {
      const msg = err.response?.data?.msg || err.response?.data?.message || 'Failed to update item status';
      toast.error(msg);
    } finally {
      setUpdatingItemId(null);
    }
  };

  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Order ${order?.order_number || ''}`} size="lg">
      {loading ? <LoadingPage /> : order && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-surface-50 p-3"><p className="text-xs text-surface-400">Status</p><StatusBadge status={order.status} /></div>
            <div className="rounded-xl bg-surface-50 p-3"><p className="text-xs text-surface-400">Total</p><p className="font-mono font-bold">₹{Number(order.total_amount).toLocaleString()}</p></div>
          </div>
          <div className="rounded-xl border border-surface-100 p-3">
            <p className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-2">Shipping</p>
            <p className="text-sm">{order.shipping_address?.full_name} — {order.shipping_address?.phone}</p>
            <p className="text-sm text-surface-500">{order.shipping_address?.address_line1}, {order.shipping_address?.city} {order.shipping_address?.pincode}</p>
          </div>
          <div className="space-y-2">
            {order.items?.map(item => (
              <div key={item.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-xl bg-surface-50 px-3 py-2.5 text-sm border border-surface-100/60">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="h-11 w-11 flex-shrink-0 overflow-hidden rounded-lg border border-surface-200 bg-white">
                    {item.product_image ? (
                      <img src={item.product_image} alt={item.product_name} className="h-full w-full object-cover" />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-surface-100 text-surface-400">
                        <ShoppingBagIcon className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.product_name}</p>
                    <div className="flex items-center gap-2 text-xs text-surface-400 mt-0.5">
                      <span>×{item.quantity}</span>
                      {item.variant_name && <Badge variant="gray" size="xs">{item.variant_name}</Badge>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between sm:justify-end gap-3 flex-shrink-0 pt-2 sm:pt-0 border-t sm:border-t-0 border-surface-100">
                  {/* Item Status Badge & quick change */}
                  <div className="flex items-center gap-1.5">
                    <StatusBadge status={item.status || order.status} />
                    {!isReadOnly && (
                      <select
                        className="text-xs py-1 px-2 rounded-lg border border-surface-200 bg-white text-surface-700 focus:outline-none focus:ring-1 focus:ring-brand-500 cursor-pointer disabled:opacity-50"
                        value={item.status || order.status || ''}
                        onChange={(e) => handleItemStatusChange(item.id, e.target.value)}
                        disabled={updatingItemId === item.id}
                        title="Change Item Status"
                      >
                        {STATUS_OPTS_BIZ.map(s => (
                          <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="text-right">
                    {/* If tax is included, total_price is the final price. If not, we add tax_amount. */}
                    <p className="font-mono font-semibold text-surface-900">
                      ₹{Number(parseFloat(item.total_price || 0) + (item.tax_included ? 0 : parseFloat(item.tax_amount || 0))).toLocaleString()}
                    </p>
                    {parseFloat(item.tax_amount) > 0 && (
                      <p className="text-[10px] text-surface-400">
                        {item.tax_included ? 'Incl.' : '+'} {item.tax_rate || 18}% GST (₹{Number(item.tax_amount).toLocaleString()})
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-surface-100 p-3 bg-surface-50/50 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-1">Price Summary</p>
            <div className="flex justify-between text-sm">
              <span className="text-surface-500">Subtotal</span>
              <span className="font-mono">₹{Number(order.items?.reduce((sum, i) => sum + parseFloat(i.total_price || 0), 0) || 0).toLocaleString()}</span>
            </div>
            {Number(order.discount_amount) > 0 && (
              <div className="flex justify-between text-sm text-success-600">
                <span>Discount</span>
                <span className="font-mono">-₹{Number(order.discount_amount).toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-surface-500">GST (Tax)</span>
              <span className="font-mono">
                {order.items?.every(i => i.tax_included) ? (
                  <span className="italic text-surface-400">Included (₹{Number(order.tax_amount).toLocaleString()})</span>
                ) : (
                  <span>₹{Number(order.tax_amount).toLocaleString()}</span>
                )}
              </span>
            </div>
            {Number(order.shipping_amount) > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-surface-500">Shipping</span>
                <span className="font-mono">₹{Number(order.shipping_amount).toLocaleString()}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-surface-500">Platform Fee</span>
              <span className="font-mono">₹20.00</span>
            </div>
            <div className="pt-2 border-t border-surface-100 flex justify-between font-bold text-base">
              <span>Total Amount</span>
              <span className="font-mono text-brand-700">₹{Number(order.total_amount).toLocaleString()}</span>
            </div>
          </div>
          {!isReadOnly && (
            <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-brand-700">Update Status</p>
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Select 
                    value={status} 
                    onChange={e => {
                      const s = e.target.value;
                      setStatus(s);
                      if (s === 'refunded' && !refundAmount) {
                        setRefundAmount(order.total_amount);
                      }
                    }}
                  >
                    <option value="">— Select Next Step —</option>
                    {(STATUS_FLOW[order.status] || []).map(s => (
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    ))}
                  </Select>
                  <input
                    className={`input ${status === 'cancelled' && !note ? 'border-error-300' : ''}`}
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder={status === 'cancelled' ? "Cancellation note (required)…" : "Note…"}
                  />
                </div>

                {status === 'refunded' && (
                  <div className="animate-in fade-in slide-in-from-top-2">
                    <p className="text-xs font-medium text-surface-500 mb-1">Refund Amount (₹)</p>
                    <input
                      type="number"
                      className="input w-full"
                      value={refundAmount}
                      onChange={e => setRefundAmount(e.target.value)}
                      placeholder="Enter amount to refund…"
                    />
                  </div>
                )}

                <Button
                  variant="primary"
                  size="sm"
                  className="w-full sm:w-auto"
                  disabled={!status || (status === 'cancelled' && !note) || (status === 'refunded' && !refundAmount)}
                  loading={updating}
                  onClick={update}
                >
                  Update Order Status
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export function BusinessOrders({ business }) {
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedId, setSelected] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const { page, setPage, reset } = usePagination();
  const { data, loading, refetch } = useQuery(
    () => ordersAPI.myOrders({ page, limit: 20, business_id: business?.id, status: statusFilter || undefined }),
    [page, statusFilter, business?.id]
  );
  const orders = data?.rows || [];
  const total = data?.count || 0;
  const pages = Math.ceil((data?.count || 0) / 20) || 1;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Orders" subtitle={`${total} orders`} />
      <Card>
        <div className="mb-5 overflow-x-auto">
          <Tabs tabs={ORDER_TABS} active={statusFilter} onChange={v => { setStatusFilter(v); reset(); }} />
        </div>
        {loading ? <LoadingPage /> : orders.length === 0 ? (
          <EmptyState icon={ShoppingBagIcon} title="No orders found" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Order #</th><th>Customer</th><th>Items</th><th>Total</th><th>Status</th><th>Date</th><th></th></tr></thead>
              <tbody>
                {orders.map(o => (
                  <tr key={o.id} className="hover:bg-surface-50/50 transition-colors cursor-default">
                    <td>
                      <button
                        onClick={() => setSelected(o.id)}
                        className="font-mono font-semibold text-brand-700 hover:text-brand-800 hover:underline transition-all decoration-2 underline-offset-2"
                        title="Click to view details"
                      >
                        {o.order_number}
                      </button>
                    </td>
                    <td>
                      <div>
                        <p className="font-medium text-sm">{o.shipping_address?.full_name || `${o.customer?.first_name || ''} ${o.customer?.last_name || ''}`.trim() || 'Unknown'}</p>
                        <p className="text-xs text-surface-400">{o.customer?.email}</p>
                      </div>
                    </td>
                    <td className="text-sm text-surface-600">{o.items?.length} item{o.items?.length !== 1 ? 's' : ''}</td>
                    <td><span className="font-mono font-semibold">₹{Number(o.total_amount).toLocaleString()}</span></td>
                    <td><StatusBadge status={o.status} /></td>
                    <td className="text-xs text-surface-400">{o.created_at ? format(new Date(o.created_at), 'dd MMM yy') : '—'}</td>
                    <td><button className="btn btn-ghost p-1.5 rounded-lg hover:bg-brand-50 hover:text-brand-600" onClick={() => setSelected(o.id)}><EyeIcon className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
      <OrderDetailModal orderId={selectedId} open={!!selectedId} hideUpdateSection={isReadOnly} onClose={() => setSelected(null)} onUpdated={refetch} />
    </div>
  );
}

export function BusinessPayouts({ business }) {
  const { page, setPage } = usePagination();
  const { data, loading } = useQuery(() => paymentsAPI.history({ page, limit: 20, business_id: business?.id }), [page, business?.id]);
  const { data: statsData } = useQuery(() => paymentsAPI.stats({ business_id: business?.id }), [business?.id]);
  const payments = data?.rows || [];
  const total = data?.count || 0;
  const pages = Math.ceil((data?.count || 0) / 20) || 1;
  const stats = statsData || {};
  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Payments & Payouts" subtitle={`${total} transactions`} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Earnings', val: stats.total_earned, color: 'green' },
          { label: 'Pending Payouts', val: stats.pending_payouts, color: 'yellow' },
          { label: 'Platform Fees', val: stats.platform_fees, color: 'purple' },
        ].map(({ label, val, color }) => (
          <Card key={label} className="p-4">
            <p className="text-xs uppercase tracking-wider text-surface-400">{label}</p>
            <p className={`font-display text-2xl font-bold mt-1 ${color === 'green' ? 'text-success-700' :
              color === 'yellow' ? 'text-warning-700' : 'text-brand-700'
              }`}>
              {val !== undefined ? `₹${Number(val).toLocaleString('en-IN')}` : '—'}
            </p>
          </Card>
        ))}
      </div>
      <Card>
        {loading ? <LoadingPage /> : payments.length === 0 ? (
          <EmptyState icon={CreditCardIcon} title="No payments yet" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>Razorpay ID</th><th>Method</th><th>Amount</th><th>Status</th><th>Date</th></tr></thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td><span className="font-mono text-xs">{p.razorpay_payment_id || p.razorpay_order_id || '—'}</span></td>
                    <td><Badge variant="gray">{p.method?.toUpperCase()}</Badge></td>
                    <td><span className="font-mono font-semibold">₹{Number(p.amount).toLocaleString()}</span></td>
                    <td><StatusBadge status={p.status} /></td>
                    <td className="text-xs text-surface-400">{p.created_at ? format(new Date(p.created_at), 'dd MMM yyyy') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

const STATUS_OPTS_RETURN = [
  'requested', 'approved', 'rejected', 'pickup_scheduled', 'return_picked', 'refunded', 'completed',
];

const ReturnDetailModal = ({ returnId, open, onClose, onUpdated }) => {
  const { data: ret, loading, refetch } = useQuery(
    () => returnsAPI.detail(returnId),
    [returnId],
    { enabled: !!returnId && open }
  );
  const [status, setStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [rejectionNote, setRejectionNote] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [pickupDate, setPickupDate] = useState('');

  const { mutate: update, loading: updating } = useMutation(
    () => returnsAPI.updateStatus(returnId, {
      status,
      admin_note: adminNote,
      rejection_note: rejectionNote,
      refund_amount: refundAmount || undefined,
      pickup_date: (status === 'pickup_scheduled' || status === 'approved') ? pickupDate : undefined,
    }),
    {
      successMsg: 'Return status updated',
      onSuccess: () => {
        refetch();
        onUpdated?.();
        setStatus(''); setAdminNote(''); setRejectionNote(''); setRefundAmount(''); setPickupDate('');
      },
    }
  );

  if (!open) return null;
  return (
    <Modal open={open} onClose={onClose} title="Return Request" size="lg">
      {loading ? <LoadingPage /> : ret && (
        <div className="space-y-4">

          {/* Info Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-xl bg-surface-50 p-3">
              <p className="text-xs text-surface-400">Status</p>
              <StatusBadge status={ret.status} />
            </div>
            <div className="rounded-xl bg-surface-50 p-3">
              <p className="text-xs text-surface-400">Resolution</p>
              <Badge variant="blue">{ret.resolution}</Badge>
            </div>
            <div className="rounded-xl bg-surface-50 p-3">
              <p className="text-xs text-surface-400">Reason</p>
              <p className="text-sm capitalize font-medium">{ret.reason?.replace(/_/g, ' ')}</p>
            </div>
            <div className="rounded-xl bg-surface-50 p-3">
              <p className="text-xs text-surface-400">Refund Amount</p>
              <p className="font-mono font-bold">{ret.refund_amount ? `₹${Number(ret.refund_amount).toLocaleString()}` : '—'}</p>
            </div>
            {ret.pickup_date && (
              <div className="rounded-xl bg-surface-50 p-3">
                <p className="text-xs text-surface-400">Pickup Date</p>
                <p className="text-sm font-medium">{format(new Date(ret.pickup_date), 'dd MMM yyyy')}</p>
              </div>
            )}
          </div>

          {/* Description */}
          {ret.description && (
            <div className="rounded-xl border border-surface-100 p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-surface-400 mb-1">Description</p>
              <p className="text-sm text-surface-600">{ret.description}</p>
            </div>
          )}

          {/* Existing Notes */}
          {(ret.admin_note || ret.rejection_note) && (
            <div className="rounded-xl border border-surface-100 p-3 space-y-2">
              {ret.admin_note && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wider text-surface-400">Admin Note</p>
                  <p className="text-sm text-surface-600">{ret.admin_note}</p>
                </>
              )}
              {ret.rejection_note && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wider text-surface-400">Rejection Note</p>
                  <p className="text-sm text-error-600">{ret.rejection_note}</p>
                </>
              )}
            </div>
          )}

          {/* Update Status Panel */}
          <div className="rounded-xl border border-brand-100 bg-brand-50 p-4 space-y-3">
            <p className="text-xs font-bold uppercase tracking-wider text-brand-700">Update Status</p>

            <Select 
              value={status} 
              onChange={e => {
                const s = e.target.value;
                setStatus(s);
                if ((s === 'refunded' || s === 'approved') && !refundAmount) {
                  const total = parseFloat(ret.item_total_price || 0);
                  const unit = parseFloat(ret.unit_price || 0);
                  const qty = parseInt(ret.quantity || 1);
                  const base = (unit * qty) || total;
                  const discount = parseFloat(ret.item_discount || 0) || (base * 0.1);
                  const netBase = base - discount;
                  const tax = parseFloat(ret.tax_amount || 0) || (netBase * (parseFloat(ret.tax_rate || 18) / 100));
                  setRefundAmount((netBase + tax).toFixed(2));
                }
              }}
            >
              <option value="">— Select Status —</option>
              {STATUS_OPTS_RETURN.map(s => (
                <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
              ))}
            </Select>

            <input
              className="input w-full"
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
              placeholder="Admin note (optional)…"
            />

            {/* Rejection note —  */}
            {status === 'rejected' && (
              <input
                className="input w-full border-error-300"
                value={rejectionNote}
                onChange={e => setRejectionNote(e.target.value)}
                placeholder="Rejection reason (required)…"
              />
            )}

            {/* Refund amount —  */}
            {(status === 'approved' || status === 'refunded') && (
              <input
                className="input w-full"
                type="number"
                value={refundAmount}
                onChange={e => setRefundAmount(e.target.value)}
                placeholder="Refund amount ₹…"
              />
            )}

            {/* Pickup Date —  */}
            {(status === 'pickup_scheduled' || status === 'approved') && (
              <div className="animate-in fade-in slide-in-from-top-2">
                <p className="text-xs font-medium text-surface-500 mb-1">Pickup Date (required)</p>
                <input
                  className="input w-full"
                  type="date"
                  value={pickupDate}
                  onChange={e => setPickupDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>
            )}

            <Button
              variant="primary"
              size="sm"
              disabled={!status || (status === 'rejected' && !rejectionNote) || ((status === 'pickup_scheduled' || status === 'approved') && !pickupDate)}
              loading={updating}
              onClick={update}
            >
              <CheckCircleIcon className="h-4 w-4 mr-1" /> Update Status
            </Button>
          </div>

        </div>
      )}
    </Modal>
  );
};

export function BusinessReturns({ business }) {
  const { page, setPage } = usePagination();
  const [selectedId, setSelected] = useState(null);
  const [viewOrderId, setViewOrderId] = useState(null);
  const { data, loading, refetch } = useQuery(
    () => returnsAPI.adminList({ page, limit: 20, business_id: business?.id }),
    [page, business?.id]
  );
  const rawReturns = data?.rows || [];
  const returns = rawReturns.map(r => {
    const total = parseFloat(r.item_total_price || 0);
    const discount = parseFloat(r.item_discount || 0);
    const tax = parseFloat(r.tax_amount || 0);
    
    // If tax is already included in total, the paid price is simply (total - discount)
    // If tax is NOT included, the paid price is (total - discount + tax)
    const computed_paid_price = r.tax_included 
      ? (total - discount) 
      : (total - discount + tax);

    return {
      ...r,
      computed_paid_price
    };
  });
  const total = data?.count || 0;
  const pages = Math.ceil((data?.count || 0) / 20) || 1;

  return (
    <div className="animate-in space-y-5">
      <SectionHeader title="Return Requests" subtitle={`${total} returns`} />
      <Card>
        {loading ? <LoadingPage /> : returns.length === 0 ? (
          <EmptyState icon={ArrowUturnLeftIcon} title="No return requests" />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr><th>Order</th><th>Item</th><th>Paid Price</th><th>Refund</th><th>Status</th><th>Date</th><th></th></tr>
              </thead>
              <tbody>
                {returns.map(r => (
                  <tr key={r.id}>
                    <td>
                      <button
                        onClick={() => setViewOrderId(r.order_id)}
                        className="font-mono text-xs font-semibold text-brand-700 hover:underline"
                        title="Click to view order details"
                      >
                        {r.order?.order_number || r.order_number || r.order_id?.substring(0, 8)}
                      </button>
                    </td>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-lg border border-surface-200 bg-white">
                          {r.product_image ? (
                            <img src={r.product_image} alt={r.product_name} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-surface-100 text-surface-400">
                              <ShoppingBagIcon className="h-5 w-5" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate max-w-[150px]">{r.product_name}</p>
                          <div className="flex flex-col gap-0.5 mt-0.5">
                            <span className="text-[10px] text-surface-400">Qty: {r.quantity}</span>
                            <span className="text-[10px] font-medium text-warning-600 italic">Reason: {r.reason?.replace(/_/g, ' ')}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p className="font-mono text-sm font-semibold text-success-700">
                        ₹{Number(r.computed_paid_price || 0).toLocaleString()}
                      </p>
                      <Badge variant="blue" className="text-[9px] py-0 px-1 mt-1">{r.resolution}</Badge>
                    </td>
                    <td className="font-mono text-sm">{r.refund_amount ? `₹${Number(r.refund_amount).toLocaleString()}` : '—'}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td className="text-xs text-surface-400">{r.created_at ? format(new Date(r.created_at), 'dd MMM yy') : '—'}</td>
                    <td>
                      <button className="btn btn-ghost p-1.5 rounded-lg" onClick={() => setSelected(r.id)}>
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} pages={pages} onPageChange={setPage} />
          </div>
        )}
      </Card>
      <ReturnDetailModal
        returnId={selectedId}
        open={!!selectedId}
        onClose={() => setSelected(null)}
        onUpdated={refetch}
      />
      <OrderDetailModal
        orderId={viewOrderId}
        open={!!viewOrderId}
        onClose={() => setViewOrderId(null)}
        readOnly={true}
      />
    </div>
  );
}